<script>
    /* Copyright (c) 2021 Jacques Desodt */

    /* This component manages a list of items (title + content) and their open status.
     Only one item content is displayed at a time.

     Note: this component is unstyled by default.
     The application is responsible for the the look and feel.
     For the best usability, see:
     https://www.w3.org/TR/wai-aria-practices/examples/accordion/accordion.html
     */
    import {createEventDispatcher, onMount} from 'svelte';

    const dispatch = createEventDispatcher();

    /* The dispatch name used when an item title is clicked */
    export let dispatchName = 'accordionItemClick';
    /* This DOM element random ID */
    export let id = 'accordion-' + Math.random().toString(36).replace(/[^a-z]+/g, '');
    /* The CSS transition rule for animations */
    export let transitionRule = 'height 300ms ease-in-out, opacity 700ms ease-in-out';

    /* The name of the accordion ID attribute. On mount, it is set for each item */
    const accordionIdAttributeName = 'data-accordion-id';
    /* The name of the dispatch name attribute. On mount, it is set for each item */
    const dispatchNameAttributeName = 'data-accordion-dispatch-name';
    /* The name of the item index attribute. On mount, it is set for each item */
    const itemIndexAttributeName = 'data-accordion-item-index';
    /* The class name used to hide an item */
    const hideClassName = 'hide';

    /* Gets the real height of a DOM element.
     The element is displayed a little fraction of time: the end user will not see anything.
     */
    const getHeight = element => {
        element.style.display = 'block'; // Make it visible
        const height = element.scrollHeight + 'px'; // Get its height
        element.style.display = ''; //  Hide it again
        return height;
    };

    /* Returns a DOM item title index */
    const getItemIndex = item => {
        return item.getAttribute(itemIndexAttributeName);
    };

    /* Returns this DOM items as a NodeList */
    const getItems = () => {
        const thisElement = document.getElementById(id);
        if (thisElement) {
            let items = [];
            /* Gets all this element accordion items, sub accordions included */
            const children = thisElement.querySelectorAll('[' + itemIndexAttributeName + ']');
            /* Loops on each child */
            for (let i = 0; i < children.length; i++) {
                const item = children[i];
                /* Retrieve the first accordion parent */
                const parent = item.closest('[data-svelte-simple-accordion]');
                /* Checks if the parent is the same (sub accordions management) */
                if (parent && parent.id === id) {
                    items.push(item);
                }
            }
            return items;
        }
        return false;
    };

    /* Initializes the items */
    const initialize = () => {
        /* Local variables */
        let index = 0;
        let toOpenItem = false;
        /* Gets this DOM items */
        const items = getItems(); // NodeList: it may not be set in this Svelte component lifecycle
        if (items) {
            /* Iterates on each DOM item */
            items.forEach(itemTitle => {
                /* Initializes the title part */
                itemTitle.setAttribute(accordionIdAttributeName, id);
                itemTitle.setAttribute(dispatchNameAttributeName, dispatchName);
                itemTitle.setAttribute(itemIndexAttributeName, index);
                /* Gets the item openAfterLoad attribute */
                const openAfterLoad = itemTitle.getAttribute('openAfterLoad');
                if (openAfterLoad === 'true') {
                    /* Memorizes the item to open */
                    toOpenItem = itemTitle;
                }
                /* Initializes the content part with the transition style */
                if (transitionRule) {
                    const itemContent = itemTitle.nextElementSibling; // the next div is the content
                    itemContent.style.transition = transitionRule;
                }
                /* Loops */
                index++;
            });
            /* Opens the selected item if needed */
            if (toOpenItem) {
                manage(toOpenItem);
            }
            /* Initializes the listen events on the custom dispatch name */
            document.getElementById(id).addEventListener(dispatchName, event => {
                manage(event.detail); // event.detail = item title part
            });
        }
        return false;
    };

    /* Opens / closes an accordion item. The other items will be closed */
    const manage = anItemTitle => { // a DOM item title part
        let opened = true; // the boolean status of the action done
        /* Gets this accordion DOM items */
        const items = getItems(); // NodeList: it may not be set in this Svelte component lifecycle
        if (items) {
            /* Iterates on each DOM item */
            items.forEach(itemTitle => {
                const toManage = getItemIndex(itemTitle) === getItemIndex(anItemTitle);
                const itemContent = itemTitle.nextElementSibling; // the next div is the content
                const toOpen = itemContent.classList.contains(hideClassName);
                /* Checks if we have to open the item content */
                if (toManage) {
                    /* Toggles the item status */
                    if (toOpen) {
                        opened = true;
                        show(itemContent);
                    } else {
                        opened = false;
                        hide(itemContent);
                    }
                } else {
                    /* We have to close the item content */
                    hide(itemContent);
                }
            });
            /* Sends the event to the application */
            dispatch(dispatchName, {
                accordion: document.getElementById(id),
                itemIndexAttributeName: itemIndexAttributeName,
                itemTitle: anItemTitle,
                opened: opened,
            });
        }
    };

    /* --------- ANIMATIONS ---------- */

    /* Hides a DOM element with animation. See:
     https://gomakethings.com/how-to-add-transition-animations-to-vanilla-javascript-show-and-hide-methods/
     */
    const hide = anElement => {
        /* Sets the real height */
        anElement.style.height = anElement.scrollHeight + 'px';
        /* Sets the height back to 0 */
        setTimeout(() => {
            anElement.style.height = '0';
        }, 10);
        /* When the transition is complet, hides the element */
        setTimeout(() => {
            anElement.classList.add(hideClassName); // hide
        }, 300);
    };

    /* Shows a DOM element with animation. See:
     https://gomakethings.com/how-to-add-transition-animations-to-vanilla-javascript-show-and-hide-methods/
     */
    const show = anElement => {
        /* Sets the real height */
        const height = getHeight(anElement);
        anElement.classList.remove(hideClassName); // show
        anElement.style.height = height;
        /* When the transition is complete, removes the height */
        setTimeout(() => {
            anElement.style.height = '';
        }, 300);
    };

    /* When the component is loaded: initializes its items */
    onMount(() => setTimeout(() => {
        initialize();
    }, 300));

</script>

<!-- This slot will contain the AccordionItem components -->
<div data-svelte-simple-accordion="true" {id}>
    <!-- Slot: the application can insert any code -->
    <slot/>
</div>
